import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertTriangle, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColor = (s) => ({
  Open: 'bg-red-100 text-red-700', 'In Process': 'bg-amber-100 text-amber-700', Closed: 'bg-gray-200 text-gray-600',
}[s] || 'bg-gray-100');
const prioColor = (p) => ({ High: 'text-red-600', Medium: 'text-amber-600', Low: 'text-gray-500' }[p] || '');
const EMPTY = { notification_type: 'Complaint', material: '', vendor: '', defect_description: '', priority: 'Medium', reference: '' };

const QualityNotifications = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('quality_notification');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [actionText, setActionText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/quality/notifications`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/quality/notifications`, form, { withCredentials: true });
      toast.success('Notification created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (number, status) => {
    try {
      const res = await axios.put(`${API}/quality/notifications/${number}`, { status }, { withCredentials: true });
      toast.success('Status updated'); setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const addAction = async () => {
    if (!actionText.trim()) return;
    try {
      const res = await axios.put(`${API}/quality/notifications/${detail.notification_number}`, { action: actionText }, { withCredentials: true });
      setDetail(res.data); setActionText(''); toast.success('Action added'); load();
    } catch (e) { toast.error('Failed to add action'); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="qm-notifications-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><AlertTriangle className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quality Notifications</h1>
              <p className="text-gray-600 text-sm mt-0.5">Defects, complaints and corrective actions.</p>
            </div>
          </div>
          {writable && <Button onClick={() => setOpen(true)} data-testid="new-notification-btn"><Plus className="h-4 w-4 mr-2" />New Notification</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Number</TableHead><TableHead>Type</TableHead><TableHead>Material</TableHead>
              <TableHead>Defect</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No notifications yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.notification_number} data-testid={`notif-row-${r.notification_number}`}>
                    <TableCell className="font-medium">{r.notification_number}</TableCell>
                    <TableCell>{r.notification_type}</TableCell>
                    <TableCell>{r.material || '—'}</TableCell>
                    <TableCell className="max-w-xs truncate">{r.defect_description}</TableCell>
                    <TableCell className={`font-medium ${prioColor(r.priority)}`}>{r.priority}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span></TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`open-notif-${r.notification_number}`}>Open</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Quality Notification</DialogTitle>
            <DialogDescription>Log a quality defect or complaint.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.notification_type} onValueChange={(v) => set('notification_type', v)}>
                <SelectTrigger data-testid="notif-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Complaint">Customer Complaint</SelectItem>
                  <SelectItem value="Vendor">Vendor Defect</SelectItem>
                  <SelectItem value="Internal">Internal Defect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger data-testid="notif-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Material</Label><Input data-testid="notif-material" value={form.material} onChange={(e) => set('material', e.target.value)} /></div>
            <div><Label>Vendor</Label><Input data-testid="notif-vendor" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} /></div>
            <div className="col-span-2"><Label>Reference</Label><Input data-testid="notif-reference" value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="GR / lot / order #" /></div>
            <div className="col-span-2"><Label>Defect Description</Label><Textarea data-testid="notif-defect" value={form.defect_description} onChange={(e) => set('defect_description', e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.defect_description} data-testid="save-notification-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.notification_number}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(detail.status)}`}>{detail.status}</span>
                </DialogTitle>
                <DialogDescription>{detail.notification_type} · {detail.priority} priority</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-500">Material:</span> {detail.material || '—'}</div>
                <div><span className="text-gray-500">Vendor:</span> {detail.vendor || '—'}</div>
                <div><span className="text-gray-500">Reference:</span> {detail.reference || '—'}</div>
                <div className="pt-1"><span className="text-gray-500">Defect:</span><p className="mt-1">{detail.defect_description}</p></div>
              </div>
              <div className="border-t pt-3">
                <h4 className="font-semibold text-sm mb-2">Actions / Log</h4>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {(detail.actions || []).length === 0 ? <p className="text-xs text-gray-400">No actions yet.</p>
                    : detail.actions.map((a, i) => (
                      <div key={i} className="text-xs bg-gray-50 rounded p-2"><span className="font-medium">{a.by}</span>: {a.text}
                        <span className="text-gray-400 ml-1">{a.at ? new Date(a.at).toLocaleString() : ''}</span></div>
                    ))}
                </div>
                {writable && (
                  <div className="flex gap-2 mt-2">
                    <Input placeholder="Add action / note" value={actionText} data-testid="notif-action-input" onChange={(e) => setActionText(e.target.value)} />
                    <Button size="sm" onClick={addAction} data-testid="add-action-btn">Add</Button>
                  </div>
                )}
              </div>
              {writable && (
                <DialogFooter className="gap-2">
                  {detail.status !== 'In Process' && <Button variant="outline" size="sm" onClick={() => updateStatus(detail.notification_number, 'In Process')} data-testid="notif-inprocess-btn">Mark In Process</Button>}
                  {detail.status !== 'Closed' && <Button size="sm" onClick={() => updateStatus(detail.notification_number, 'Closed')} data-testid="notif-close-btn">Close</Button>}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default QualityNotifications;
